import React, { useEffect, useMemo, useState } from 'react'
import { PageHeader } from '@/components/ui/PageHeader'
import {
  AutomationSequence,
  AutomationStep,
  AuthProfile,
  StepInput,
  createSequence,
  createStep,
  deleteSequence,
  deleteStep,
  listActions,
  listSequences,
  testFire,
  updateSequence,
  updateStep,
} from '@/api/labAutomation'
import { useNavigate } from 'react-router-dom'

// Feature #262: Lab-wide Automation Sequences admin page (platform_admin
// only). The page handles list, create, edit, step builder, and the
// link out to run history. Test-fire + replay live on the run-history
// page (AutomationRuns.tsx).
//
// Layout: a left list of sequences (filterable by source_module +
// trigger_event_type + enabled flag) and a right edit pane that toggles
// between "create new", "edit existing", and "step builder" modes.
// Action config is a JSON textarea for v1 — each registered handler
// will eventually expose its own typed schema, at which point the
// editor can render a per-action form. Generic JSON keeps the surface
// open without forcing a rewrite of the runtime.

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const monoTextarea: React.CSSProperties = {
  ...inputStyle,
  fontFamily: 'monospace',
  resize: 'vertical',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}

const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

const dangerBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: '#dc3545',
}

const AUTH_PROFILES: AuthProfile[] = ['none', 'graph_app', 'bearer_static', 'internal']

interface DraftSequence {
  name: string
  description: string
  sourceModule: string
  triggerEventType: string
  triggerFilterRaw: string
  retryMaxAttempts: number
  retryBackoffSeconds: number
  isEnabled: boolean
}

const emptyDraftSequence: DraftSequence = {
  name: '',
  description: '',
  sourceModule: 'tap',
  triggerEventType: '',
  triggerFilterRaw: '',
  retryMaxAttempts: 3,
  retryBackoffSeconds: 60,
  isEnabled: false,
}

interface DraftStep {
  stepOrder: number
  actionType: string
  authProfile: AuthProfile
  configRaw: string
  responseCaptureRaw: string
  continueOnFailure: boolean
  timeoutSeconds: number
}

const emptyDraftStep: DraftStep = {
  stepOrder: 0,
  actionType: 'http.request',
  authProfile: 'none',
  configRaw: '{}',
  responseCaptureRaw: '',
  continueOnFailure: false,
  timeoutSeconds: 30,
}

const AutomationSequences: React.FC = () => {
  const navigate = useNavigate()
  const [sequences, setSequences] = useState<AutomationSequence[]>([])
  const [actionTypes, setActionTypes] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [filterSource, setFilterSource] = useState('')
  const [filterEvent, setFilterEvent] = useState('')
  const [filterEnabled, setFilterEnabled] = useState<'' | 'true' | 'false'>('')

  const [selectedId, setSelectedId] = useState<string | 'new' | null>(null)
  const [draft, setDraft] = useState<DraftSequence>(emptyDraftSequence)
  const [saving, setSaving] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  // Step editor state — the step list lives off the loaded sequence;
  // only the in-progress draft is local.
  const [editingStepId, setEditingStepId] = useState<string | 'new' | null>(null)
  const [stepDraft, setStepDraft] = useState<DraftStep>(emptyDraftStep)

  const selected = useMemo(() => {
    if (!selectedId || selectedId === 'new') return null
    return sequences.find((s) => s.id === selectedId) || null
  }, [sequences, selectedId])

  const refresh = async () => {
    setLoading(true)
    try {
      const data = await listSequences({
        sourceModule: filterSource || undefined,
        triggerEventType: filterEvent || undefined,
        enabled: filterEnabled === '' ? undefined : filterEnabled === 'true',
      })
      setSequences(data)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    refresh()
  }, [filterSource, filterEvent, filterEnabled])

  useEffect(() => {
    listActions().then(setActionTypes).catch(() => setActionTypes([]))
  }, [])

  const startNew = () => {
    setSelectedId('new')
    setDraft(emptyDraftSequence)
    setErrorMsg(null)
    setEditingStepId(null)
  }

  const selectSequence = (id: string) => {
    const s = sequences.find((x) => x.id === id)
    if (!s) return
    setSelectedId(id)
    setErrorMsg(null)
    setEditingStepId(null)
    setDraft({
      name: s.name,
      description: s.description || '',
      sourceModule: s.sourceModule,
      triggerEventType: s.triggerEventType,
      triggerFilterRaw: s.triggerFilter ? JSON.stringify(s.triggerFilter, null, 2) : '',
      retryMaxAttempts: s.retryMaxAttempts,
      retryBackoffSeconds: s.retryBackoffSeconds,
      isEnabled: s.isEnabled,
    })
  }

  const cancel = () => {
    setSelectedId(null)
    setDraft(emptyDraftSequence)
    setErrorMsg(null)
    setEditingStepId(null)
  }

  const parseFilter = (raw: string): Record<string, unknown> | null => {
    if (!raw.trim()) return null
    return JSON.parse(raw)
  }

  const saveSequence = async () => {
    setSaving(true)
    setErrorMsg(null)
    try {
      let filter: Record<string, unknown> | null
      try {
        filter = parseFilter(draft.triggerFilterRaw)
      } catch (e) {
        setErrorMsg(`trigger_filter is not valid JSON: ${(e as Error).message}`)
        setSaving(false)
        return
      }
      if (selectedId === 'new') {
        const created = await createSequence({
          name: draft.name,
          description: draft.description || null,
          sourceModule: draft.sourceModule,
          triggerEventType: draft.triggerEventType,
          triggerFilter: filter,
          retryMaxAttempts: draft.retryMaxAttempts,
          retryBackoffSeconds: draft.retryBackoffSeconds,
          isEnabled: draft.isEnabled,
        })
        await refresh()
        setSelectedId(created.id)
      } else if (selectedId) {
        await updateSequence(selectedId, {
          name: draft.name,
          description: draft.description || null,
          sourceModule: draft.sourceModule,
          triggerEventType: draft.triggerEventType,
          triggerFilter: filter,
          retryMaxAttempts: draft.retryMaxAttempts,
          retryBackoffSeconds: draft.retryBackoffSeconds,
          isEnabled: draft.isEnabled,
        })
        await refresh()
      }
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErrorMsg(detail || (err as Error).message || 'Failed to save sequence')
    } finally {
      setSaving(false)
    }
  }

  const deleteSelected = async () => {
    if (!selectedId || selectedId === 'new') return
    if (!confirm('Soft-delete this sequence? Existing run history is preserved.')) return
    await deleteSequence(selectedId)
    cancel()
    await refresh()
  }

  // ── Step editor ────────────────────────────────────────────────
  const startNewStep = () => {
    if (!selected) return
    const nextOrder = (selected.steps[selected.steps.length - 1]?.stepOrder ?? -1) + 1
    setEditingStepId('new')
    setStepDraft({ ...emptyDraftStep, stepOrder: nextOrder })
  }

  const editStep = (s: AutomationStep) => {
    setEditingStepId(s.id)
    setStepDraft({
      stepOrder: s.stepOrder,
      actionType: s.actionType,
      authProfile: s.authProfile,
      configRaw: JSON.stringify(s.actionConfig || {}, null, 2),
      responseCaptureRaw: s.responseCapture ? JSON.stringify(s.responseCapture, null, 2) : '',
      continueOnFailure: s.continueOnFailure,
      timeoutSeconds: s.timeoutSeconds,
    })
  }

  const saveStep = async () => {
    if (!selected || !editingStepId) return
    setSaving(true)
    setErrorMsg(null)
    try {
      let actionConfig: Record<string, unknown>
      try {
        actionConfig = JSON.parse(stepDraft.configRaw || '{}')
      } catch (e) {
        setErrorMsg(`action_config is not valid JSON: ${(e as Error).message}`)
        setSaving(false)
        return
      }
      let responseCapture: Record<string, string> | null = null
      if (stepDraft.responseCaptureRaw.trim()) {
        try {
          responseCapture = JSON.parse(stepDraft.responseCaptureRaw)
        } catch (e) {
          setErrorMsg(`response_capture is not valid JSON: ${(e as Error).message}`)
          setSaving(false)
          return
        }
      }
      const payload: StepInput = {
        stepOrder: stepDraft.stepOrder,
        actionType: stepDraft.actionType,
        actionConfig,
        authProfile: stepDraft.authProfile,
        responseCapture,
        continueOnFailure: stepDraft.continueOnFailure,
        timeoutSeconds: stepDraft.timeoutSeconds,
      }
      if (editingStepId === 'new') {
        await createStep(selected.id, payload)
      } else {
        await updateStep(selected.id, editingStepId, payload)
      }
      setEditingStepId(null)
      await refresh()
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErrorMsg(detail || (err as Error).message || 'Failed to save step')
    } finally {
      setSaving(false)
    }
  }

  const removeStep = async (stepId: string) => {
    if (!selected) return
    if (!confirm('Remove this step?')) return
    await deleteStep(selected.id, stepId)
    await refresh()
  }

  const reorderStep = async (step: AutomationStep, direction: 'up' | 'down') => {
    if (!selected) return
    const newOrder = step.stepOrder + (direction === 'up' ? -1 : 1)
    if (newOrder < 0) return
    await updateStep(selected.id, step.id, { stepOrder: newOrder })
    await refresh()
  }

  // ── Test-fire ──────────────────────────────────────────────────
  const [testEntityType, setTestEntityType] = useState('tap_application')
  const [testEntityId, setTestEntityId] = useState('')
  const [testPayloadRaw, setTestPayloadRaw] = useState('{}')
  const [testFiring, setTestFiring] = useState(false)

  const fireTest = async () => {
    if (!selected) return
    setTestFiring(true)
    setErrorMsg(null)
    try {
      let payload: Record<string, unknown>
      try {
        payload = JSON.parse(testPayloadRaw || '{}')
      } catch (e) {
        setErrorMsg(`payload is not valid JSON: ${(e as Error).message}`)
        return
      }
      const result = await testFire(selected.id, {
        sourceEntityType: testEntityType,
        sourceEntityId: testEntityId,
        payload,
      })
      const runId = result.run_ids[0]
      if (runId) {
        navigate(`/automation/runs/${runId}`)
      }
    } catch (err) {
      const detail = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setErrorMsg(detail || (err as Error).message || 'Test fire failed')
    } finally {
      setTestFiring(false)
    }
  }

  return (
    <div>
      <PageHeader
        title="Automation Sequences"
        subtitle="Lab-wide event-driven automations (Feature #262)"
      />

      <div style={{ display: 'grid', gridTemplateColumns: '360px 1fr', gap: '16px', marginTop: '16px' }}>
        {/* Left: list + filters */}
        <div style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
            <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Sequences</h3>
            <button onClick={startNew} style={primaryBtn}>+ New</button>
          </div>

          <div style={{ display: 'grid', gap: '8px', marginBottom: '12px' }}>
            <input
              type="text"
              placeholder="Filter source module"
              value={filterSource}
              onChange={(e) => setFilterSource(e.target.value)}
              style={inputStyle}
            />
            <input
              type="text"
              placeholder="Filter trigger event type"
              value={filterEvent}
              onChange={(e) => setFilterEvent(e.target.value)}
              style={inputStyle}
            />
            <select
              value={filterEnabled}
              onChange={(e) => setFilterEnabled(e.target.value as '' | 'true' | 'false')}
              style={inputStyle}
            >
              <option value="">All sequences</option>
              <option value="true">Enabled only</option>
              <option value="false">Disabled only</option>
            </select>
          </div>

          {loading && <div style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>Loading…</div>}
          {!loading && sequences.length === 0 && (
            <div style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>No sequences match.</div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {sequences.map((s) => {
              const active = s.id === selectedId
              return (
                <button
                  key={s.id}
                  onClick={() => selectSequence(s.id)}
                  style={{
                    textAlign: 'left',
                    padding: '8px 10px',
                    border: active ? '1px solid var(--color-magenta)' : '1px solid transparent',
                    borderRadius: '6px',
                    backgroundColor: active ? 'rgba(214, 51, 132, 0.08)' : 'transparent',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family)',
                  }}
                >
                  <div style={{ fontSize: '13px', fontWeight: 600, color: 'var(--color-black)' }}>{s.name}</div>
                  <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)' }}>
                    {s.sourceModule}/{s.triggerEventType} · {s.steps.length} step(s) ·{' '}
                    {s.isEnabled ? <span style={{ color: '#16a34a' }}>enabled</span> : <span>disabled</span>}
                  </div>
                </button>
              )
            })}
          </div>
        </div>

        {/* Right: editor */}
        <div style={cardStyle}>
          {selectedId === null && (
            <div style={{ color: 'var(--color-dark-grey)', fontSize: '13px' }}>
              Select a sequence on the left, or click "New" to create one.
            </div>
          )}
          {selectedId !== null && (
            <>
              {errorMsg && (
                <div
                  style={{
                    marginBottom: '12px',
                    padding: '8px 12px',
                    backgroundColor: 'rgba(220, 53, 69, 0.1)',
                    color: '#dc3545',
                    borderRadius: '6px',
                    fontSize: '13px',
                  }}
                >
                  {errorMsg}
                </div>
              )}

              <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>
                {selectedId === 'new' ? 'New sequence' : `Edit: ${draft.name || '(unnamed)'}`}
              </h3>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={labelStyle}>Name</label>
                  <input
                    type="text"
                    value={draft.name}
                    onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Source module</label>
                  <input
                    type="text"
                    value={draft.sourceModule}
                    onChange={(e) => setDraft({ ...draft, sourceModule: e.target.value })}
                    placeholder="tap"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Trigger event type</label>
                  <input
                    type="text"
                    value={draft.triggerEventType}
                    onChange={(e) => setDraft({ ...draft, triggerEventType: e.target.value })}
                    placeholder="OutcomeChange"
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Enabled</label>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', height: '38px' }}>
                    <input
                      type="checkbox"
                      checked={draft.isEnabled}
                      onChange={(e) => setDraft({ ...draft, isEnabled: e.target.checked })}
                    />
                    <span style={{ fontSize: '13px' }}>Run this sequence on matching events</span>
                  </label>
                </div>
                <div style={{ gridColumn: '1 / span 2' }}>
                  <label style={labelStyle}>Description</label>
                  <input
                    type="text"
                    value={draft.description}
                    onChange={(e) => setDraft({ ...draft, description: e.target.value })}
                    style={inputStyle}
                  />
                </div>
                <div style={{ gridColumn: '1 / span 2' }}>
                  <label style={labelStyle}>Trigger filter (JSON predicate, optional)</label>
                  <textarea
                    rows={5}
                    value={draft.triggerFilterRaw}
                    onChange={(e) => setDraft({ ...draft, triggerFilterRaw: e.target.value })}
                    placeholder='{"op": "eq", "field": "to", "value": "Successful"}'
                    style={monoTextarea}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Retry max attempts</label>
                  <input
                    type="number"
                    value={draft.retryMaxAttempts}
                    onChange={(e) =>
                      setDraft({ ...draft, retryMaxAttempts: Number(e.target.value) || 0 })
                    }
                    style={inputStyle}
                  />
                </div>
                <div>
                  <label style={labelStyle}>Retry backoff (sec)</label>
                  <input
                    type="number"
                    value={draft.retryBackoffSeconds}
                    onChange={(e) =>
                      setDraft({ ...draft, retryBackoffSeconds: Number(e.target.value) || 0 })
                    }
                    style={inputStyle}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '16px' }}>
                {selectedId !== 'new' && (
                  <button onClick={deleteSelected} disabled={saving} style={dangerBtn}>
                    Delete
                  </button>
                )}
                <button onClick={cancel} disabled={saving} style={secondaryBtn}>
                  Cancel
                </button>
                <button onClick={saveSequence} disabled={saving} style={primaryBtn}>
                  {saving ? 'Saving…' : selectedId === 'new' ? 'Create' : 'Save'}
                </button>
              </div>

              {/* Step builder — only for existing sequences */}
              {selected && (
                <div style={{ marginTop: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <h4 style={{ margin: 0, fontSize: '14px', fontWeight: 600 }}>Steps ({selected.steps.length})</h4>
                    <button onClick={startNewStep} style={primaryBtn}>+ Add step</button>
                  </div>
                  {selected.steps.length === 0 && (
                    <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                      No steps configured yet — click "Add step" to define the first action.
                    </div>
                  )}
                  {selected.steps.map((s) => (
                    <div
                      key={s.id}
                      style={{
                        border: '1px solid var(--color-light-grey)',
                        borderRadius: '6px',
                        padding: '10px',
                        marginBottom: '8px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                      }}
                    >
                      <div>
                        <span style={{ fontFamily: 'monospace', fontSize: '12px', marginRight: '8px' }}>
                          #{s.stepOrder}
                        </span>
                        <strong style={{ fontSize: '13px' }}>{s.actionType}</strong>
                        <span style={{ fontSize: '11px', color: 'var(--color-dark-grey)', marginLeft: '8px' }}>
                          auth: {s.authProfile} · timeout: {s.timeoutSeconds}s
                          {s.continueOnFailure && ' · continue-on-failure'}
                        </span>
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button onClick={() => reorderStep(s, 'up')} style={{ ...secondaryBtn, padding: '4px 8px' }}>↑</button>
                        <button onClick={() => reorderStep(s, 'down')} style={{ ...secondaryBtn, padding: '4px 8px' }}>↓</button>
                        <button onClick={() => editStep(s)} style={{ ...secondaryBtn, padding: '4px 10px' }}>Edit</button>
                        <button onClick={() => removeStep(s.id)} style={{ ...dangerBtn, padding: '4px 10px' }}>Remove</button>
                      </div>
                    </div>
                  ))}

                  {editingStepId !== null && (
                    <div style={{ ...cardStyle, marginTop: '12px', backgroundColor: 'var(--color-light-grey)' }}>
                      <h4 style={{ margin: '0 0 12px 0', fontSize: '13px', fontWeight: 600 }}>
                        {editingStepId === 'new' ? 'New step' : 'Edit step'}
                      </h4>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                        <div>
                          <label style={labelStyle}>Step order</label>
                          <input
                            type="number"
                            value={stepDraft.stepOrder}
                            onChange={(e) =>
                              setStepDraft({ ...stepDraft, stepOrder: Number(e.target.value) || 0 })
                            }
                            style={inputStyle}
                          />
                        </div>
                        <div>
                          <label style={labelStyle}>Action type</label>
                          <select
                            value={stepDraft.actionType}
                            onChange={(e) => setStepDraft({ ...stepDraft, actionType: e.target.value })}
                            style={inputStyle}
                          >
                            {actionTypes.map((at) => (
                              <option key={at} value={at}>
                                {at}
                              </option>
                            ))}
                            {!actionTypes.includes(stepDraft.actionType) && (
                              <option value={stepDraft.actionType}>{stepDraft.actionType}</option>
                            )}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Auth profile</label>
                          <select
                            value={stepDraft.authProfile}
                            onChange={(e) =>
                              setStepDraft({ ...stepDraft, authProfile: e.target.value as AuthProfile })
                            }
                            style={inputStyle}
                          >
                            {AUTH_PROFILES.map((ap) => (
                              <option key={ap} value={ap}>
                                {ap}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label style={labelStyle}>Timeout (sec)</label>
                          <input
                            type="number"
                            value={stepDraft.timeoutSeconds}
                            onChange={(e) =>
                              setStepDraft({ ...stepDraft, timeoutSeconds: Number(e.target.value) || 30 })
                            }
                            style={inputStyle}
                          />
                        </div>
                        <div style={{ gridColumn: '1 / span 2' }}>
                          <label style={labelStyle}>action_config (JSON)</label>
                          <textarea
                            rows={10}
                            value={stepDraft.configRaw}
                            onChange={(e) => setStepDraft({ ...stepDraft, configRaw: e.target.value })}
                            style={monoTextarea}
                          />
                        </div>
                        <div style={{ gridColumn: '1 / span 2' }}>
                          <label style={labelStyle}>response_capture (JSONPath bindings, optional)</label>
                          <textarea
                            rows={3}
                            value={stepDraft.responseCaptureRaw}
                            onChange={(e) => setStepDraft({ ...stepDraft, responseCaptureRaw: e.target.value })}
                            placeholder='{"folder_id": "$.id", "web_url": "$.webUrl"}'
                            style={monoTextarea}
                          />
                        </div>
                        <div>
                          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '24px' }}>
                            <input
                              type="checkbox"
                              checked={stepDraft.continueOnFailure}
                              onChange={(e) =>
                                setStepDraft({ ...stepDraft, continueOnFailure: e.target.checked })
                              }
                            />
                            <span style={{ fontSize: '13px' }}>Continue on failure</span>
                          </label>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', marginTop: '12px' }}>
                        <button onClick={() => setEditingStepId(null)} style={secondaryBtn}>
                          Cancel
                        </button>
                        <button onClick={saveStep} disabled={saving} style={primaryBtn}>
                          {saving ? 'Saving…' : editingStepId === 'new' ? 'Add step' : 'Save step'}
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Test-fire panel */}
                  <div style={{ marginTop: '24px' }}>
                    <h4 style={{ margin: '0 0 12px 0', fontSize: '14px', fontWeight: 600 }}>Test fire</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                      <div>
                        <label style={labelStyle}>Source entity type</label>
                        <input
                          type="text"
                          value={testEntityType}
                          onChange={(e) => setTestEntityType(e.target.value)}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={labelStyle}>Source entity id</label>
                        <input
                          type="text"
                          value={testEntityId}
                          onChange={(e) => setTestEntityId(e.target.value)}
                          placeholder="UUID of an existing entity"
                          style={inputStyle}
                        />
                      </div>
                      <div style={{ gridColumn: '1 / span 2' }}>
                        <label style={labelStyle}>Synthetic payload (JSON)</label>
                        <textarea
                          rows={5}
                          value={testPayloadRaw}
                          onChange={(e) => setTestPayloadRaw(e.target.value)}
                          style={monoTextarea}
                        />
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', justifyContent: 'space-between', alignItems: 'center', marginTop: '12px' }}>
                      <button
                        onClick={() => navigate(`/automation/sequences/${selected.id}/runs`)}
                        style={secondaryBtn}
                      >
                        View run history →
                      </button>
                      <button onClick={fireTest} disabled={testFiring || !testEntityId} style={primaryBtn}>
                        {testFiring ? 'Firing…' : 'Test fire'}
                      </button>
                    </div>
                    <div style={{ fontSize: '11px', color: 'var(--color-dark-grey)', marginTop: '8px' }}>
                      Test-fire bypasses the trigger filter so the sequence runs regardless of whether
                      the synthetic payload matches.
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export default AutomationSequences
